<script setup lang="ts">
import { debounce } from "lodash";
import { computed, ref, watch } from "vue";
import VSelect from "vue-select";
import SearchIcon from "@/components/shared/icons/SearchIcon.vue";
import SkeletonLoader from "@/components/shared/loaders/BaseSkeletonLoader.vue";
import type { Props, SchoolOption } from "./types";

defineOptions({
  name: "SchoolSearchSelect",
});

const props = withDefaults(defineProps<Props>(), {
  label: "",
  placeholder: "Type to search your university ...",
  description: "",
  variant: "compact",
  disabled: false,
  noDrop: false,
  clearable: true,
  loading: false,
  errorMessage: "",
  optionLabel: "name",
  testId: "",
  searchDebounceMs: 700,
  noResultsText: "Sorry, no matching results",
  loadingText: "Loading, please wait…",
});

const emit = defineEmits<{
  (e: "update:modelValue", value: SchoolOption | null): void;
  (e: "search", payload: { search: string; loading: (state: boolean) => void }): void;
}>();

const selected = ref<SchoolOption | null>(props.modelValue);

watch(selected, (next) => {
  emit("update:modelValue", next);
});

watch(
  () => props.modelValue,
  (next) => {
    selected.value = next;
  },
);

const componentClasses = computed<string>(() =>
  props.variant === "heading" ? "flex flex-col" : "flex flex-col gap-2",
);

const labelClasses = computed<string>(() =>
  props.variant === "heading"
    ? "block text-lg md:font-semibold mt-2 mb-1"
    : "block text-sm font-medium text-indigo-base",
);

const descriptionClasses = computed(() =>
  props.variant === "heading" ? "block mb-2 text-sm" : "block text-sm font-normal",
);

const hasInlineErrors = computed(() => (props.vuelidateField?.$errors?.length ?? 0) > 0);

const showError = computed(
  () => Boolean(props.errorMessage) || Boolean(props.vuelidateField?.$error) || hasInlineErrors.value,
);

const isSearchPending = ref(false);

const debouncedSearch = debounce((searchTerm: string, setLoading: (state: boolean) => void) => {
  isSearchPending.value = false;
  emit("search", { search: searchTerm, loading: setLoading });
}, props.searchDebounceMs);

const onSearch = (searchTerm: string, setLoading: (state: boolean) => void) => {
  isSearchPending.value = searchTerm.length > 0;
  debouncedSearch(searchTerm, setLoading);
};

const handleBlur = () => {
  props.vuelidateField?.$touch?.();
};

const optionTestId = (option: SchoolOption) => {
  const label = String(option[props.optionLabel] ?? "").replace(/\s+/g, "-");
  return `${label}-option`;
};
</script>

<template>
  <div :class="componentClasses">
    <label v-if="label" :for="props.id" :class="labelClasses">
      {{ label }}
    </label>

    <p v-if="description" :class="descriptionClasses" data-testid="school-search-description">
      {{ description }}
    </p>

    <SkeletonLoader v-if="loading" rounded />

    <VSelect
      v-else
      :id="props.id"
      v-model="selected"
      :options="options"
      :placeholder="placeholder"
      :disabled="disabled"
      :clearable="clearable && !disabled"
      :no-drop="noDrop"
      :get-option-label="(option: SchoolOption) => String(option[props.optionLabel] ?? '')"
      :data-testid="testId || 'school-search-select'"
      :class="{
        'custom-select': true,
        'custom-select-error': showError,
      }"
      @search="onSearch"
      @search:blur="handleBlur"
    >
      <template #open-indicator="{ attributes }">
        <span v-bind="attributes">
          <SearchIcon :size="20" />
        </span>
      </template>
      <template #option="option">
        <div
          :data-testid="optionTestId(option)"
          class="whitespace-normal break-words overflow-wrap-anywhere"
        >
          {{ option[props.optionLabel] }}
        </div>
      </template>
      <template #no-options="{ search, searching, loading: vsLoading }">
        <div class="py-2 text-sm text-gray-600">
          <template v-if="vsLoading || isSearchPending">{{ loadingText }}</template>
          <template v-else-if="searching && search.length">{{ noResultsText }}</template>
          <template v-else>{{ placeholder }}</template>
        </div>
      </template>
    </VSelect>

    <p
      v-if="errorMessage"
      class="error text-error-900 text-sm"
      data-testid="school-search-error"
    >
      {{ errorMessage }}
    </p>
    <p
      v-for="error of props.vuelidateField?.$errors ?? []"
      :key="error.$uid"
      class="error text-error-900 text-sm"
    >
      {{ error.$message }}
    </p>
  </div>
</template>

<style scoped>
.custom-select :deep(.vs__dropdown-toggle) {
  @reference pt-3 pb-4 rounded-none border-indigo-base;
  max-height: 3.15rem;
}

.custom-select :deep(.vs__search) {
  @reference m-0;
}

.custom-select-error :deep(.vs__dropdown-toggle) {
  @reference border-error-900;
}

.custom-select :deep(.vs__open-indicator) {
  transform: none;
  transition: none;
}
</style>
